/**
 * JSON-LD builders.
 *
 * Structured data is how Google and the AI answer engines (AI Overviews,
 * ChatGPT Search, Perplexity) read a page without guessing at the DOM, so
 * every route ships an explicit graph rather than relying on inference.
 */
import { SITE_NAME, SITE_URL, absoluteUrl } from "../data/site";

const LOGO = SITE_URL + "/logo.png";

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": SITE_URL + "/#organization",
    name: SITE_NAME,
    alternateName: ["Bagad Satta", "BagadSattaCompany", "Bagad Satta King"],
    url: SITE_URL + "/",
    logo: {
      "@type": "ImageObject",
      "@id": SITE_URL + "/#logo",
      url: LOGO,
      contentUrl: LOGO,
      caption: SITE_NAME,
    },
    image: { "@id": SITE_URL + "/#logo" },
    areaServed: { "@type": "Country", name: "India" },
    knowsLanguage: ["hi", "en"],
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": SITE_URL + "/#website",
    url: SITE_URL + "/",
    name: SITE_NAME,
    description:
      "Live daily results and monthly record charts for Disawar, Bagad, Delhi Bazar, Faridabad, Ghaziabad, Gali, Gogamedi, Jai Bhawani, Jai Hanuman and Shri Ganesh.",
    publisher: { "@id": SITE_URL + "/#organization" },
    inLanguage: ["en-IN", "hi-IN"],
  };
}

export function webPageSchema({ path, title, description, modifiedTime }) {
  const url = absoluteUrl(path);
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": url + "#webpage",
    url,
    name: title,
    description,
    isPartOf: { "@id": SITE_URL + "/#website" },
    about: { "@id": SITE_URL + "/#organization" },
    inLanguage: "en-IN",
    ...(modifiedTime ? { dateModified: modifiedTime } : {}),
  };
}

/**
 * Breadcrumbs give Google the site hierarchy and replace the bare URL in the
 * SERP with a readable path, which lifts click-through on deep pages.
 */
export function breadcrumbSchema(trail) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((crumb, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: crumb.name,
      item: absoluteUrl(crumb.path),
    })),
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/**
 * A game's daily result is a published number tied to a date and a time — an
 * ItemList of dated entries is the closest honest description of that, and it
 * makes the freshness of the page machine-readable.
 */
export function resultListSchema({ game, path, rows, dateISO }) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": absoluteUrl(path) + "#results",
    name: `${game.name} Satta Result Chart`,
    description: `Declared ${game.name} results with the daily result time of ${game.timeLabel} IST.`,
    numberOfItems: rows.length,
    itemListOrder: "https://schema.org/ItemListOrderDescending",
    ...(dateISO ? { dateModified: dateISO } : {}),
    itemListElement: rows.map((row, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: `${game.name} result ${row.date}`,
      description: `${game.name} result for ${row.date} is ${row.value}.`,
    })),
  };
}

/** Marks the site as a software/app publisher for the Android app CTA. */
export function appSchema(appUrl) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Bagad Satta App",
    operatingSystem: "Android",
    applicationCategory: "EntertainmentApplication",
    url: appUrl,
    publisher: { "@id": SITE_URL + "/#organization" },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
  };
}
